import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Star, House, Pencil, Trash, MapPin, X, Camera, CrosshairSimple, Spinner, MapTrifold } from "@phosphor-icons/react";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import MapPicker from "../components/MapPicker";
import { processAndUploadImage } from "../lib/imageUpload";
import { isNative } from "../lib/native";
import { getGeoPosition, geoErrorMessage, getGeoDiagnostics } from "../lib/geolocation";
import { reverseGeocode as reverseGeocodeOsm } from "../lib/geo";
import { useLocation as useAppLocation } from "../lib/location";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import { PhotoSourceSheet } from "../components/PhotoSourceSheet";
import { GeoProgressBar } from "../components/GeoProgressBar";

const UF_LIST = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const EMPTY = {
  label: "", street: "", number: "", neighborhood: "",
  city: "", uf: "MT", cep: "", complement: "", photo: "", is_default: false,
  lat: null, lng: null,
};

function maskCEP(v) {
  return v.replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
}
const onlyDigits = (v) => v.replace(/\D/g, "");

// V18.9.17 — coordenadas do CEP (BrasilAPI v2 devolve lat/lng de boa parte dos CEPs).
// Serve para o mapa abrir já perto do local em vez do centro da cidade.
async function fetchCepCoords(rawCep) {
  const cep = onlyDigits(rawCep);
  if (cep.length !== 8) return null;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 7000);
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`, { signal: ctrl.signal });
    if (!res.ok) return null;
    const d = await res.json();
    const lat = parseFloat(d?.location?.coordinates?.latitude);
    const lng = parseFloat(d?.location?.coordinates?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

async function fetchViaCep(rawCep) {
  const cep = onlyDigits(rawCep);
  if (cep.length !== 8) return null;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 9000);
  try {
    const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: ctrl.signal });
    const d = await res.json();
    if (d.erro) return null;
    return {
      street: d.logradouro || "",
      neighborhood: d.bairro || "",
      city: d.localidade || "",
      uf: d.uf || "",
      cep: maskCEP(cep),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

export default function Addresses() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [photoSheet, setPhotoSheet] = useState(false);
  const [geoDebug, setGeoDebug] = useState(null);
  const [cepCenter, setCepCenter] = useState(null);
  const { location: appLoc } = useAppLocation();
  const { user, loading: authLoading } = useAuth();
  const { setOpen: openAuthModal } = useAuthModal();

  // V12.5: monta a query de endereço p/ geocoding/mapa
  // V18.9.12 — sem nenhum campo preenchido, cai na cidade atual do app (antes
  // mandava só ", Brasil" para o Nominatim e o mapa abria no centro do país).
  const addrQuery = () => {
    const base = [form.street, form.number, form.neighborhood, form.city, form.uf]
      .map((s) => (s || "").trim()).filter(Boolean).join(", ");
    if (!base) {
      const c = [appLoc?.city, appLoc?.uf].filter(Boolean).join(", ");
      return c ? `${c}, Brasil` : "";
    }
    return (form.cep ? `${base}, ${form.cep}` : base) + ", Brasil";
  };
  // V7.1: Redirect-back flow — recebe `returnTo` na query string e seleciona
  // o endereço recém-criado para a tela de origem
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/addresses");
      setItems(r.data);
    } catch (e) { console.error("[Addresses] load failed", e?.message); }
  }, []);

  useEffect(() => { if (user) load(); }, [load, user]);
  useFocusRefresh(load, []);
  const ptr = usePullToRefresh(load);

  // Se chegou com returnTo, abre o form direto e mostra um aviso
  useEffect(() => {
    if (returnTo) {
      setForm(EMPTY); setEditing(null); setShowForm(true);
    }
  }, [returnTo]);

  const startNew = () => { setForm(EMPTY); setEditing(null); setCepCenter(null); setShowForm(true); };
  const startEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditing(a.id); setCepCenter(null); setShowForm(true); };

  const detectLocation = async () => {
    setDetecting(true);
    setGeoDebug(null);
    toast.message("Detectando localização...");
    try {
      const pos = await getGeoPosition({ timeout: 15000, enableHighAccuracy: true, maximumAge: 30000 });
      const { latitude, longitude } = pos.coords;
      const d = await reverseGeocodeOsm(latitude, longitude);
      // As coordenadas são o mais importante (frete/raio) — salvamos mesmo se o
      // reverse-geocode falhar; o usuário completa os campos na mão.
      setForm((f) => ({
        ...f,
        street: (d && d.street) || f.street,
        neighborhood: (d && d.neighborhood) || f.neighborhood,
        city: (d && d.city) || f.city,
        uf: (d && d.uf) || f.uf,
        cep: (d && d.cep) || f.cep,
        number: (d && d.house_number) || f.number,
        lat: latitude,
        lng: longitude,
      }));
      if (d && d.city) toast.success("Localização detectada! Confira o número e complemento.");
      else toast.warning("Pegamos seu GPS, mas não achamos o endereço. Digite o CEP para completar.");
    } catch (err) {
      toast.error(geoErrorMessage(err));
      setGeoDebug(getGeoDiagnostics());
    } finally {
      setDetecting(false);
    }
  };

  // V7.1: Quando CEP completa 8 dígitos, chama ViaCEP automaticamente
  const onCepChange = async (e) => {
    const newCep = maskCEP(e.target.value);
    setForm((f) => ({ ...f, cep: newCep }));
    if (onlyDigits(newCep).length === 8) {
      setCepLoading(true);
      const d = await fetchViaCep(newCep);
      setCepLoading(false);
      if (d) {
        setForm((f) => ({
          ...f,
          street: d.street || f.street,
          neighborhood: d.neighborhood || f.neighborhood,
          city: d.city || f.city,
          uf: d.uf || f.uf,
        }));
        toast.success("Endereço preenchido pelo CEP. Confira e adicione o número.");
      } else {
        toast.warning("CEP não encontrado. Preencha manualmente.");
      }
      // V18.9.17 — busca as COORDENADAS do CEP para o mapa já abrir praticamente no
      // local certo (antes o mapa caía na cidade e o usuário tinha que arrastar tudo).
      fetchCepCoords(newCep).then((c) => { if (c) setCepCenter(c); });
    }
  };

  const upd = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
  };

  const doPhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await processAndUploadImage(file);
      setForm((f) => ({ ...f, photo: data.path }));
    } catch (err) {
      toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onPhoto = (e) => doPhotoUpload(e.target.files?.[0]);

  // App nativo: abre o menu padronizado (Câmera/Galeria); no web usa o <input type="file">.
  const capturePhotoNative = () => setPhotoSheet(true);

  const save = async (e) => {
    e.preventDefault();
    // Obrigatório: local marcado no mapa (garante lat/lng sempre — frete/raio dependem disso)
    if (!form.lat || !form.lng) {
      toast.error("Marque o local exato no mapa para salvar o endereço.");
      setShowMap(true);
      return;
    }
    setBusy(true);
    try {
      const payload = form;
      let saved;
      if (editing) {
        const r = await api.patch(`/addresses/${editing}`, payload);
        saved = r.data;
        toast.success("Endereço atualizado");
      } else {
        const r = await api.post("/addresses", payload);
        saved = r.data;
        toast.success("Endereço cadastrado");
      }
      setShowForm(false);
      load();

      // V7.1: Se veio de outro fluxo (ex.: criando anúncio), volta com o ID
      if (returnTo && saved?.id) {
        // Concatena ?selectedAddressId=... ao returnTo de forma segura
        const sep = returnTo.includes("?") ? "&" : "?";
        navigate(`${returnTo}${sep}selectedAddressId=${saved.id}`, { replace: true });
      }
    } catch (err) {
      // V18.9.12 — 401 aqui significa sessão perdida (o erro cru "Não autenticado"
      // não dizia nada ao usuário). Abre o login e preserva o formulário preenchido.
      if (err?.response?.status === 401) {
        toast.error("Sua sessão expirou. Entre na sua conta para salvar o endereço.");
        openAuthModal(true);
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este endereço?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Excluído");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setDefault = async (id) => {
    await api.patch(`/addresses/${id}`, { is_default: true });
    load();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="addresses-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      {!user && !authLoading && (
        <div className="aj-card p-4 mb-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm flex flex-col gap-2" data-testid="addresses-auth-required">
          <span className="font-bold">Entre na sua conta para gerenciar endereços.</span>
          <button onClick={() => openAuthModal(true)} className="btn-primary text-sm py-2 px-4 self-start" data-testid="addresses-login-btn">
            Entrar / criar conta
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading font-black text-2xl">Meus Endereços</h1>
        <button onClick={startNew} className="btn-primary text-sm py-2 px-4" data-testid="new-address-btn">
          <Plus size={16} weight="bold" /> Novo
        </button>
      </div>

      {returnTo && !showForm && (
        <div className="aj-card p-3 mb-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="return-to-notice">
          📝 Você está cadastrando um novo endereço para continuar com outra ação.
        </div>
      )}

      {items.length === 0 ? (
        <div className="aj-card p-10 text-center text-slate-500">
          <House size={36} weight="duotone" className="mx-auto text-slate-300" />
          <p className="mt-2">Cadastre um endereço para criar anúncios.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="aj-card p-4 flex gap-3" data-testid={`address-${a.id}`}>
              <div className="w-20 h-20 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                {a.photo ? (
                  <img src={fileUrl(a.photo)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><MapPin size={26} weight="duotone" className="text-slate-300" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-base truncate">{a.label}</div>
                  {a.is_default && <span className="aj-badge-success aj-badge text-[10px]">Padrão</span>}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {a.street}, {a.number} {a.complement && `- ${a.complement}`}
                </div>
                <div className="text-xs text-slate-500">
                  {a.neighborhood} · {a.city} - {a.uf} · CEP {a.cep}
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {!a.is_default && (
                    <button onClick={() => setDefault(a.id)} className="text-[11px] text-[#004E98] font-bold" data-testid={`set-default-${a.id}`}>
                      Tornar padrão
                    </button>
                  )}
                  <button onClick={() => startEdit(a)} className="text-[11px] text-slate-500 font-bold inline-flex items-center gap-1" data-testid={`edit-address-${a.id}`}>
                    <Pencil size={11} weight="bold" /> Editar
                  </button>
                  <button onClick={() => del(a.id)} className="text-[11px] text-red-600 font-bold inline-flex items-center gap-1" data-testid={`delete-address-${a.id}`}>
                    <Trash size={11} weight="bold" /> Excluir
                  </button>
                  {returnTo && (
                    <button
                      onClick={() => {
                        const sep = returnTo.includes("?") ? "&" : "?";
                        navigate(`${returnTo}${sep}selectedAddressId=${a.id}`, { replace: true });
                      }}
                      className="text-[11px] text-emerald-700 font-bold inline-flex items-center gap-1"
                      data-testid={`use-address-${a.id}`}
                    >
                      ✓ Usar este
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && setShowForm(false)} data-testid="address-form-overlay">
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl p-6 max-h-[92vh] overflow-y-auto space-y-3"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-xl">{editing ? "Editar" : "Novo"} endereço</h3>
              <button type="button" onClick={() => setShowForm(false)} data-testid="close-address-form"><X size={20} weight="bold" /></button>
            </div>
            <input required placeholder="Apelido (ex: Minha Casa)" value={form.label} onChange={upd("label")} className="aj-input" data-testid="addr-label" />

            <button
              type="button"
              onClick={detectLocation}
              disabled={detecting}
              className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-full border-2 border-[#004E98] text-[#004E98] font-bold text-sm hover:bg-[#004E98]/5 disabled:opacity-60"
              data-testid="detect-location-btn"
            >
              {detecting ? (
                <><Spinner size={16} className="animate-spin" /> Detectando...</>
              ) : (
                <><CrosshairSimple size={16} weight="bold" /> Usar minha localização atual</>
              )}
            </button>

            <GeoProgressBar active={detecting} expectedMs={12000} testId="addr-geo-progress" />

            {geoDebug && (
              <details className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="addr-geo-debug">
                <summary className="cursor-pointer font-bold text-slate-600">Diagnóstico da localização (para o suporte)</summary>
                <pre className="mt-1 whitespace-pre-wrap break-all text-slate-500">{JSON.stringify(geoDebug, null, 1)}</pre>
              </details>
            )}


            <div className="relative">
              <input
                required
                placeholder="CEP (preenche o resto automaticamente)"
                value={form.cep}
                onChange={onCepChange}
                className="aj-input"
                inputMode="numeric"
                data-testid="addr-cep"
              />
              {cepLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner size={16} className="animate-spin text-[#004E98]" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <input required placeholder="Rua" value={form.street} onChange={upd("street")} className="aj-input col-span-2" data-testid="addr-street" />
              <input required placeholder="Nº" value={form.number} onChange={upd("number")} className="aj-input" data-testid="addr-number" inputMode="numeric" />
            </div>
            <input placeholder="Complemento (opcional)" value={form.complement} onChange={upd("complement")} className="aj-input" data-testid="addr-complement" />
            <input required placeholder="Bairro" value={form.neighborhood} onChange={upd("neighborhood")} className="aj-input" data-testid="addr-neighborhood" />
            <div className="grid grid-cols-3 gap-2">
              <input required placeholder="Cidade" value={form.city} onChange={upd("city")} className="aj-input col-span-2" data-testid="addr-city" />
              <select value={form.uf} onChange={upd("uf")} className="aj-input" data-testid="addr-uf">
                {UF_LIST.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>

            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Foto da fachada (opcional)</span>
              <div className="mt-1 flex items-center gap-3">
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:bg-slate-50 relative overflow-hidden" data-testid="addr-photo-label">
                  <input type="file" accept="image/*" onClick={(e) => { if (isNative()) { e.preventDefault(); capturePhotoNative(); } }} onChange={onPhoto} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                  {form.photo ? (
                    <img src={fileUrl(form.photo)} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Camera size={20} weight="bold" className="text-slate-400" />
                  )}
                </label>
                <span className="text-xs text-slate-500">{uploading ? "Subindo..." : form.photo ? "Foto adicionada" : "Toque para anexar"}</span>
              </div>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="addr-default" />
              Tornar endereço padrão
            </label>

            <button
              type="button"
              onClick={() => setShowMap(true)}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 font-bold text-sm ${
                form.lat && form.lng
                  ? "border-emerald-500 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                  : "border-[#FF6B35] text-[#FF6B35] bg-[#FF6B35]/5 hover:bg-[#FF6B35]/10"
              }`}
              data-testid="open-map-btn"
            >
              <MapTrifold size={18} weight="bold" />
              {form.lat && form.lng ? "✓ Local marcado — ajustar no mapa" : "Marcar local no mapa (obrigatório)"}
            </button>

            {form.lat && form.lng ? (
              <div className="text-[10px] text-emerald-600 text-center font-semibold" data-testid="addr-coords">
                📍 Local definido: {Number(form.lat).toFixed(5)}, {Number(form.lng).toFixed(5)}
              </div>
            ) : (
              <div className="text-[10px] text-[#FF6B35] text-center font-semibold" data-testid="addr-coords-missing">
                Ajuste fino do alfinete no mapa — necessário para calcular frete e distância.
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="save-address-btn">
              {busy ? "Salvando..." : "Salvar endereço"}
            </button>
          </form>
        </div>
      )}

      {showMap && (
        <MapPicker
          initial={form.lat && form.lng ? { lat: Number(form.lat), lng: Number(form.lng) } : null}
          fallbackCenter={cepCenter || (appLoc?.lat && appLoc?.lng ? { lat: appLoc.lat, lng: appLoc.lng } : null)}
          fallbackZoom={cepCenter ? 17 : 14}
          addressQuery={addrQuery()}
          onConfirm={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
          onClose={() => setShowMap(false)}
        />
      )}

      <PhotoSourceSheet
        open={photoSheet}
        onClose={() => setPhotoSheet(false)}
        onPick={(files) => { if (files?.[0]) doPhotoUpload(files[0]); }}
      />
    </div>
  );
}
